import { useEffect, useState, useRef } from "react";
import { imageUrl } from "../lib/images";

export default function LightboxModal({ images = [], initialIndex = 0, isOpen, onClose, captions = [] }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowRight") {
        setCurrentIndex((prev) => (prev + 1) % images.length);
      } else if (e.key === "ArrowLeft") {
        setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, images.length, onClose]);

  if (!isOpen || images.length === 0) return null;

  const handleTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (distance > minSwipeDistance) {
      // Swipe left -> Next
      setCurrentIndex((prev) => (prev + 1) % images.length);
    } else if (distance < -minSwipeDistance) {
      // Swipe right -> Prev
      setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-md transition-opacity duration-300"
      onClick={onClose}
    >
      {/* Top action bar */}
      <div className="absolute top-0 inset-x-0 flex items-center justify-between px-6 py-5 z-20">
        <div className="text-xs font-mono tracking-widest text-slate-300 bg-slate-900/80 px-3 py-1.5 rounded border border-slate-800 flex items-center gap-3">
          <span className="text-[#2DD4BF] font-semibold">{currentIndex + 1} / {images.length}</span>
          {captions[currentIndex] && (
            <span className="hidden sm:inline border-l border-slate-700 pl-3 text-slate-200 font-sans normal-case">
              {captions[currentIndex]}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          aria-label="Luk visning"
          className="flex items-center gap-2 text-slate-300 hover:text-white px-3 py-1.5 rounded-md bg-slate-900/80 hover:bg-slate-800 border border-slate-800 transition cursor-pointer"
        >
          <span className="text-xs uppercase tracking-wider font-semibold">Luk</span>
          <i className="fa-solid fa-xmark text-sm" aria-hidden="true" />
        </button>
      </div>

      {/* Prev button */}
      {images.length > 1 && (
        <button
          onClick={prevImage}
          aria-label="Forrige billede"
          className="absolute left-4 md:left-8 z-20 w-12 h-12 flex items-center justify-center rounded-full bg-slate-900/80 hover:bg-[#0F766E] text-slate-200 hover:text-white border border-slate-700 transition cursor-pointer"
        >
          <i className="fa-solid fa-chevron-left text-base" aria-hidden="true" />
        </button>
      )}

      {/* Main Image Container */}
      <div
        className="relative max-w-5xl max-h-[80vh] w-full px-4 flex items-center justify-center select-none"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <img
          src={imageUrl(images[currentIndex], "width=1200,quality=90,format=auto")}
          alt={captions[currentIndex] || `KVC Express materiel visning ${currentIndex + 1}`}
          className="max-h-[75vh] w-auto max-w-full object-contain rounded-lg border border-slate-800 shadow-2xl transition-all duration-200"
        />
      </div>

      {/* Next button */}
      {images.length > 1 && (
        <button
          onClick={nextImage}
          aria-label="Næste billede"
          className="absolute right-4 md:right-8 z-20 w-12 h-12 flex items-center justify-center rounded-full bg-slate-900/80 hover:bg-[#0F766E] text-slate-200 hover:text-white border border-slate-700 transition cursor-pointer"
        >
          <i className="fa-solid fa-chevron-right text-base" aria-hidden="true" />
        </button>
      )}

      {/* Bottom thumbnails / instructions */}
      <div className="absolute bottom-4 inset-x-0 flex items-center justify-center gap-2 px-4 z-20">
        <div className="hidden sm:flex items-center gap-2 bg-slate-950/80 px-4 py-2 rounded-full border border-slate-800">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex(idx);
              }}
              className={`w-3 h-3 rounded-full transition-all cursor-pointer ${
                idx === currentIndex ? "bg-[#2DD4BF] w-8" : "bg-slate-600 hover:bg-slate-400"
              }`}
              aria-label={`Gå til billede ${idx + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
